import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import useSeo from '../hooks/useSeo';
import SectionHeading from '../components/SectionHeading';
import Icon from '../components/Icon';
import ProcessSteps from '../components/ProcessSteps';
import WhyInterscope from '../components/WhyInterscope';
import GlobalGrowth from '../components/GlobalGrowth';
import CountUp from '../components/CountUp';
import CTA from '../components/CTA';
import { about, team } from '../mock/mock';
import { performanceStats } from '../lib/liveData';

export default function About() {
  useReveal('about');
  useSeo({ title: 'About | Interscope Ads Performance Marketing Agency', description: 'Interscope is a global performance marketing and mobile user acquisition agency. Who we are, what we specialize in and our data-driven approach.' });
  return (
    <main className="pt-32">
      <section className="relative max-w-7xl mx-auto px-5 md:px-8 pb-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="reveal">
            <span className="inline-block mb-3 text-xs font-semibold tracking-[0.25em] uppercase text-[#2563eb]">About Interscope</span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.08]">
              A global, data-driven <span className="text-gradient">growth partner.</span>
            </h1>
            <p className="mt-6 text-slate-600 text-lg leading-relaxed">{about.intro}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contact" className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#2563eb] to-[#1d4ed8] px-7 py-3.5 font-semibold text-white hover:opacity-90 transition-opacity blue-glow">
                Work with us <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
          <div className="reveal relative">
            <div className="absolute -inset-4 bg-[#2563eb]/10 blur-3xl rounded-full" aria-hidden />
            <img src="https://images.unsplash.com/flagged/photo-1576485436509-a7d286952b65?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Interscope team" className="relative rounded-3xl border border-slate-200 object-cover w-full h-[420px]" loading="lazy" />
          </div>
        </div>
      </section>

      <section className="relative max-w-7xl mx-auto px-5 md:px-8 py-12">
        <div className="reveal glass-strong rounded-3xl p-8 md:p-10">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-4">
            {performanceStats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="font-display text-3xl md:text-4xl font-bold text-gradient"><CountUp end={s.value} suffix={s.suffix} /></div>
                <div className="mt-2 text-xs md:text-sm text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative max-w-7xl mx-auto px-5 md:px-8 section-pad">
        <SectionHeading eyebrow="Our Approach" title="What sets us apart" subtitle="Four pillars that drive every engagement we take on." />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {about.points.map((v) => (
            <div key={v.title} className="reveal glass glass-hover rounded-2xl p-7">
              <div className="w-12 h-12 rounded-xl bg-[#2563eb]/12 grid place-items-center text-[#2563eb] mb-5"><Icon name={v.icon} size={24} /></div>
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-2">{v.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="relative max-w-7xl mx-auto px-5 md:px-8 pb-4">
        <SectionHeading eyebrow="How It Works" title="Acquire. Optimize. Scale." subtitle="A transparent, measurable path from strategy to scale." />
        <ProcessSteps />
      </section>

      <WhyInterscope />
      <GlobalGrowth />

      <section className="relative max-w-7xl mx-auto px-5 md:px-8 section-pad">
        <SectionHeading eyebrow="The People" title="Meet the experts" subtitle="Specialists across strategy, media and technology." />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {team.map((m) => (
            <div key={m.name} className="reveal group rounded-2xl overflow-hidden glass glass-hover">
              <div className="h-56 overflow-hidden">
                <img src={m.img} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
              <div className="p-5">
                <h3 className="font-display text-lg font-semibold text-slate-900">{m.name}</h3>
                <p className="text-sm text-[#2563eb]">{m.role}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <CTA />
    </main>
  );
}
